import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Info, AlertTriangle, CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const alertVariants = cva(
  'flex gap-3 rounded-md border px-4 py-3 text-sm',
  {
    variants: {
      variant: {
        info:    'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200',
        warning: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
        success: 'border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950/40 dark:text-green-200',
        error:   'border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200',
      },
    },
    defaultVariants: { variant: 'info' },
  },
);

const iconMap = {
  info:    Info,
  warning: AlertTriangle,
  success: CheckCircle2,
  error:   AlertCircle,
} as const;

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  title?: string;
}

export function Alert({ className, variant = 'info', title, children, ...props }: AlertProps) {
  const Icon = iconMap[variant ?? 'info'];
  return (
    <div className={cn(alertVariants({ variant }), className)} role="alert" {...props}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium leading-none mb-1">{title}</p>}
        {children && <p className="leading-relaxed">{children}</p>}
      </div>
    </div>
  );
}
